import { DateTime } from 'luxon';

import { getDayOfWeekIndex } from '../getOrderedDaysOfWeek';
import { getMonthIndex } from '../getOrderedMonths';

/**
 * Builds the cell-level comparator used for all non-grouping columns.
 * 1D matrices compare formatted string values (with numeric parsing);
 * 2D matrices do the same but simpler (no date/month/day-of-week
 * fallbacks because those are only in the grouping column).
 */
export const buildCellComparator = ({
  isTwoDimensional,
  weekStartsOn,
}: {
  isTwoDimensional: boolean;
  weekStartsOn: WeekStartsOn;
}) => {
  if (isTwoDimensional) {
    return (a: any, b: any) => {
      if (a === null || a === undefined || a === '-') return -1;
      if (b === null || b === undefined || b === '-') return 1;
      const parsedA = Number(String(a).replace(/[$,%]/g, ''));
      const parsedB = Number(String(b).replace(/[$,%]/g, ''));
      if (!isNaN(parsedA) && !isNaN(parsedB)) return parsedA - parsedB;
      return String(a).localeCompare(String(b));
    };
  }

  return (a: any = '-', b: any = '', nodeA: any, nodeB: any) => {
    const parsedA = Number(a?.replace(/[$,%]/g, ''));
    const parsedB = Number(b?.replace(/[$,%]/g, ''));

    // If sorting by date column, use raw date instead of formatted date
    if (a === nodeA?.data['date'] && b === nodeB?.data['date']) {
      const dateA = DateTime.fromISO(nodeA?.data['raw-date']);
      const dateB = DateTime.fromISO(nodeB?.data['raw-date']);

      return dateA.diff(dateB).as('milliseconds');
    }

    if (!isNaN(parsedA) && !isNaN(parsedB)) {
      return parsedA - parsedB;
    }

    const monthIndexA = getMonthIndex(a);
    const monthIndexB = getMonthIndex(b);
    if (monthIndexA !== -1 && monthIndexB !== -1) {
      return monthIndexA - monthIndexB;
    }

    const dayIndexA = getDayOfWeekIndex({ value: a, weekStartsOn });
    const dayIndexB = getDayOfWeekIndex({ value: b, weekStartsOn });
    if (dayIndexA !== -1 && dayIndexB !== -1) {
      return dayIndexA - dayIndexB;
    }

    return a.localeCompare(b);
  };
};

/**
 * Builds the comparator for the grouping (left-hand) column.
 * For month/day-of-week dimensions it sorts by canonical order;
 * otherwise it falls back to the cell comparator.
 */
export const buildGroupComparator = ({
  chartDef,
  weekStartsOn,
  cellComparator,
}: {
  chartDef: V5ChartDefinition;
  weekStartsOn: WeekStartsOn;
  cellComparator: (a: any, b: any, nodeA?: any, nodeB?: any) => number;
}) => {
  const isMonth = chartDef.dimensionA && chartDef.dimensionA.isGroupByMonth;
  const isDayOfWeek =
    chartDef.dimensionA && chartDef.dimensionA.isGroupByDayOfWeek;

  if (!isMonth && !isDayOfWeek) {
    return cellComparator;
  }

  if (isDayOfWeek) {
    return (a: any, b: any) => {
      const dayIndexA = getDayOfWeekIndex({
        value: String(a),
        weekStartsOn,
      });
      const dayIndexB = getDayOfWeekIndex({
        value: String(b),
        weekStartsOn,
      });
      if (dayIndexA !== -1 && dayIndexB !== -1) {
        return dayIndexA - dayIndexB;
      }
      return 0;
    };
  }

  return (a: any, b: any) => {
    const monthIndexA = getMonthIndex(String(a));
    const monthIndexB = getMonthIndex(String(b));
    if (monthIndexA !== -1 && monthIndexB !== -1) {
      return monthIndexA - monthIndexB;
    }
    return 0;
  };
};
